// tools-add-remove-runner.ts
//
// Thực thi bulk add/remove tool: discover → chọn (checkbox hoặc chọn-hết) →
// loop enable/disableToolByName. Dùng bởi `avatar tools add`/`remove` VÀ bởi
// cú pháp `avatar tools enable all`/`disable all` (delegate all:true — DRY,
// không thêm call-site mới tới hub CRITICAL enableToolByName/disableToolByName).
//
// Tách khỏi tools.ts để giữ file đăng ký lệnh <200 dòng + runner test được
// mà không cần đăng ký commander.

import { resolve } from "node:path";
import { checkbox, confirm } from "@inquirer/prompts";
import { discoverEnabledAndAvailableTools } from "../lib/discover-pack-tools-and-defaults.js";
import { log } from "../lib/terminal-logger.js";
import {
  buildAddChoices,
  buildRemoveChoices,
  resolveNonInteractiveSelection,
} from "../lib/tool-add-remove-selection.js";
import { disableToolByName, enableToolByName } from "../lib/tool-enable-disable-orchestrator.js";

export interface ToolOptions {
  target?: string;
}

export interface AddRemoveOptions extends ToolOptions {
  all?: boolean;
  yes?: boolean;
}

export function resolveWorkspace(opts: ToolOptions): string {
  return resolve(opts.target ?? process.cwd());
}

// Quyết định selection: --all hoặc non-TTY → chọn hết candidate; ngược lại prompt
// checkbox. Trả về danh sách tên tool đã chọn (có thể rỗng).
async function selectTools(
  candidates: string[],
  preChecked: string[],
  opts: AddRemoveOptions,
  verb: "add" | "remove",
): Promise<string[]> {
  const nonInteractive = opts.all === true || !process.stdout.isTTY;
  if (nonInteractive) {
    return resolveNonInteractiveSelection(candidates);
  }
  const choices =
    verb === "add" ? buildAddChoices(candidates, preChecked) : buildRemoveChoices(candidates);
  return await checkbox<string>({
    message:
      verb === "add"
        ? "Chọn tool để cài (space chọn, enter xác nhận):"
        : "Chọn tool để gỡ (space chọn, enter xác nhận):",
    choices,
  });
}

// `avatar tools add` (và `enable all` khi opts.all=true) — list available,
// chọn 1-n, enable từng cái. Bulk best-effort: 1 tool bị skip (vd ai-orchestrator
// cần TTY picker riêng) KHÔNG chặn các tool còn lại — luôn kết thúc exit 0.
// enableToolByName tự log lý do cụ thể khi trả false (thiếu manifest, non-TTY,
// user hủy, fetch fail, 1-model...) — không cần hint chung ở đây (từng bị lệch
// hướng/double-log khi mọi lý do false đều đã có message riêng).
export async function runAdd(opts: AddRemoveOptions): Promise<void> {
  const ws = resolveWorkspace(opts);
  const { available, enabled } = await discoverEnabledAndAvailableTools(ws);
  if (available.length === 0) {
    log.dim("Không có tool available (pack chưa có tools/, hoặc chưa sync).");
    return;
  }
  const selected = await selectTools(available, enabled, opts, "add");
  if (selected.length === 0) {
    log.dim("Không chọn tool nào — hủy, không thay đổi.");
    return;
  }
  for (const name of selected) {
    await enableToolByName(ws, name);
  }
}

// `avatar tools remove` (và `disable all` khi opts.all=true) — list enabled,
// chọn 1-n, disable từng cái. all=true → KHÔNG confirm (đồng nhất `remove --all`
// và `disable <name>` đơn — gõ literal 'all' đã là opt-in bulk tường minh).
export async function runRemove(opts: AddRemoveOptions): Promise<void> {
  const ws = resolveWorkspace(opts);
  const { enabled } = await discoverEnabledAndAvailableTools(ws);
  if (enabled.length === 0) {
    log.dim("Không có tool nào đang bật — không có gì để gỡ.");
    return;
  }
  const selected = await selectTools(enabled, [], opts, "remove");
  if (selected.length === 0) {
    log.dim("Không chọn tool nào — hủy, không thay đổi.");
    return;
  }
  // remove sửa settings.json → xác nhận trước (trừ khi --yes, --all, hoặc non-TTY).
  const needConfirm = opts.yes !== true && opts.all !== true && process.stdout.isTTY;
  if (needConfirm) {
    const ok = await confirm({
      message: `Gỡ ${selected.length} tool: ${selected.join(", ")}?`,
      default: true,
    });
    if (!ok) {
      log.dim("Hủy — không gỡ tool nào.");
      return;
    }
  }
  for (const name of selected) {
    await disableToolByName(ws, name);
  }
}
